/**
 * [QA-04] Cross-tab session identity sync.
 *
 * The session lives in httpOnly cookies shared by every tab of the
 * browser, but each tab keeps its own in-memory identity (auth store) and
 * its own React Query cache, whose keys name the resource, not the viewer
 * (`['admin-overview']`, `['workspace-overview']` …). When one tab signed
 * out, signed in as someone else or switched company, the OTHER tabs kept
 * the old header and permission gates while their polls fetched the NEW
 * session's data — one company's numbers rendered under another
 * company's name.
 *
 * Every tab that changes the session publishes the resulting identity to
 * localStorage. Other tabs hear it through the `storage` event (fired
 * only in OTHER tabs) and re-check on focus / visibility in case a frozen
 * tab missed it. A signed-in tab whose identity no longer matches drops
 * its cache and reloads, so it re-bootstraps as whoever the cookies now
 * belong to (or lands on sign-in).
 *
 * Dependency-free (storage / window / document are injectable) so it runs
 * under `node --test` (tests/identity-sync.test.js).
 */

export const IDENTITY_STORAGE_KEY = 'taskmagix.identity'

/** Stable identity key for a signed-in user: `${userId}:${tenantId}`.
 *  Empty string when nobody is signed in. */
export function identityKeyOf(user) {
  if (!user?.id) return ''
  return `${user.id}:${user.tenantId ?? ''}`
}

function defaultStorage() {
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}

/** Record the identity the browser's session cookies now belong to.
 *  `null` user → signed out. Never throws (storage may be blocked). */
export function publishIdentity(user, storage = defaultStorage()) {
  try {
    storage?.setItem(IDENTITY_STORAGE_KEY, identityKeyOf(user))
  } catch {
    /* storage blocked — cross-tab sync degrades, nothing else breaks */
  }
}

/** Last published identity, or `null` when nothing was ever published
 *  (or storage is unavailable) — i.e. no evidence either way. */
export function readPublishedIdentity(storage = defaultStorage()) {
  try {
    return storage?.getItem(IDENTITY_STORAGE_KEY) ?? null
  } catch {
    return null
  }
}

/** True when THIS tab is signed in as someone other than the identity the
 *  shared session cookies were last published for. Tabs that are still
 *  bootstrapping or signed out have nothing stale to show. */
export function isIdentityStale({ status, user } = {}, published) {
  if (status !== 'authenticated') return false
  if (published == null) return false
  return published !== identityKeyOf(user)
}

/** Wire the listeners. `getState` returns `{ status, user }`; `onStale`
 *  runs at most once per detected change. Returns an uninstall function. */
export function installIdentitySync({
  getState,
  onStale,
  win = globalThis.window,
  doc = globalThis.document,
  storage = defaultStorage(),
}) {
  if (!win || typeof win.addEventListener !== 'function') return () => {}
  let fired = false
  const check = () => {
    if (fired) return
    if (isIdentityStale(getState(), readPublishedIdentity(storage))) {
      fired = true
      onStale()
    }
  }
  const onStorage = (event) => {
    // `key === null` → storage.clear() in another tab.
    if (event?.key === IDENTITY_STORAGE_KEY || event?.key === null) check()
  }
  const onVisible = () => {
    if (!doc || doc.visibilityState !== 'hidden') check()
  }
  win.addEventListener('storage', onStorage)
  win.addEventListener('focus', onVisible)
  doc?.addEventListener?.('visibilitychange', onVisible)
  return () => {
    win.removeEventListener('storage', onStorage)
    win.removeEventListener('focus', onVisible)
    doc?.removeEventListener?.('visibilitychange', onVisible)
  }
}
